import React, { useState } from "react";
import styles from "../devicebackup.module.scss";

import { Button, Input, Modal, Upload, Select, Form, message, Row, Col } from "antd";
import { UploadOutlined } from "@ant-design/icons";

import SparkMD5 from "spark-md5";
import { useTranslation } from "react-i18next";

import { CharacterVerify } from "../../../Utility/RegExp";

import { useUploadRepository } from "../queries";
import { useConfig } from "Components/Navbar/queries";

const { Option, OptGroup } = Select;

interface uploadFileModalProps {
  stateCid: string;
  level: string;
  loginCid: string;
  visible: boolean;
  modelList: string[];
  setVisible: (arg: boolean) => void;
}
// 用於顯示一個對話框，用戶可以通過這個對話框上傳文件到存儲庫
const UploadFileModal = React.memo(
  ({ stateCid, level, loginCid, visible, modelList, setVisible }: uploadFileModalProps) => {
    const { mutateAsync: uploadFileMutation, isLoading: uploadFileLoading } = useUploadRepository(stateCid);
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState<any[]>([]);
    const [uploadFileType, setUploadFileType] = useState<string | undefined>(undefined);
    const { data: config } = useConfig();
    const modelMap = config?.modelMap || {};

    //取得副檔名
    const fileExtension = (filename: any) => (/[.]/.exec(filename) ? /[^.]+$/.exec(filename)?.[0] : undefined);

    //選擇檔案時的行為
    const handleChange = (info: any) => {
      let fileList = [...info.fileList];
      fileList = fileList.slice(-1);

      if (fileList?.[0]?.size > 100000000) {
        return;
      }

      //@ts-ignore
      var blobSlice = File.prototype.slice || File.prototype.mozSlice || File.prototype.webkitSlice,
        file = info.file,
        chunkSize = 2097152, // Read in chunks of 2MB
        chunks = Math.ceil(file.size / chunkSize),
        currentChunk = 0,
        spark = new SparkMD5.ArrayBuffer(),
        fileReader = new FileReader();

      fileReader.onload = function (e: any) {
        spark.append(e.target.result); // Append array buffer
        currentChunk++;
        let cMD5;
        if (currentChunk < chunks) {
          loadNext();
        } else {
          cMD5 = spark.end();
          form.setFieldsValue({ md5: cMD5 });
        }

        const Extension = fileExtension(fileList?.[0]?.name);
        if (Extension === "tgz" && level !== "set") {
          form.setFieldsValue({ type: "cfg" });
        } else if (Extension === "img" && level !== "set") {
          form.setFieldsValue({ type: "fw" });
          setUploadFileType("fw");
        } else if (Extension === "yml") {
          form.setFieldsValue({ type: "yml" });
        } else {
          form.setFieldsValue({ type: "Others" });
        }
      };

      //model mapping
      const ModelName = Object.entries(modelMap).filter(([_, value]) => fileList?.[0]?.name.includes(value))[0]?.[0];
      form.setFieldsValue({ model: ModelName });
      // 錯誤處理: 如果上傳過程中出現錯誤，例如選擇了太大的文件，系統會顯示相應的錯誤消息，並取消上傳操作。
      fileReader.onerror = function () {
        console.warn("oops, something went wrong.");
      };

      function loadNext() {
        var start = currentChunk * chunkSize,
          end = start + chunkSize >= file.size ? file.size : start + chunkSize;
        fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
      }

      loadNext();
      setFileList(fileList);
      setUploadFileType("");
    };

    //傳入Upload組件
    const props = {
      onRemove: (file: any) => {
        const index = fileList.indexOf(file);
        const newFileList = fileList.slice();
        newFileList.splice(index, 1);
        setFileList(newFileList);
      },
      onChange: handleChange,
      beforeUpload: (file: any) => {
        if (file.size > 100000000) {
          setFileList([]);
          message.error("file is too large (>100MB)");
          return;
        }
        setFileList([...fileList, file]);
        return false;
      },
      fileList,
    };

    const UploadOnFinish = (values: any) => {
      uploadFileMutation({ level, stateCid, loginCid, values })
        .then(() => {
          message.success(t("ISMS.Success"));
        })
        .catch(() => {
          message.error(t("ISMS.Fail"));
        })
        .finally(() => {
          form.resetFields();
          setUploadFileType("");
          setFileList([]);
        });
    };

    return (
      <Modal
        title={t("ISMS.Upload")}
        visible={visible}
        onCancel={() => {
          setVisible(false);
          form.resetFields();
          //@ts-ignore
          props.onRemove();
          setUploadFileType(undefined);
        }}
        // 開始上傳文件
        footer={[
          <Button
            key="submit"
            type="primary"
            disabled={fileList.length === 0}
            loading={uploadFileLoading}
            onClick={() => {
              form.submit();
            }}
          >
            {uploadFileLoading ? t("ISMS.Uploading") : t("ISMS.StartUpload")}
          </Button>,
        ]}
      >
        <Form onFinish={UploadOnFinish} form={form} layout="vertical">
          <div className="formwrap">
            <Row gutter={24} justify="center">
              <Col xs={24} sm={24} md={24} lg={20} xl={20}>
                <Form.Item
                  label={t("ISMS.File")}
                  name={"upload"}
                  rules={[{ required: true, message: "Please select a file" }]}
                >
                  {/* @ts-ignore */}
                  <Upload {...props} maxCount={1}>
                    <Button icon={<UploadOutlined />}>{t("ISMS.SelectFile")}</Button>
                  </Upload>
                </Form.Item>
                <Form.Item
                  label={t("ISMS.Model")}
                  name={"model"}
                  rules={[{ required: true, message: "Please select a model" }]}
                >
                  <Select placeholder={t("ISMS.Select")}>
                    {modelList.map((item: any, index: number) => {
                      return (
                        <Option key={index} value={item}>
                          {item}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
                <Form.Item label={t("ISMS.FileName")} name={"filename"} rules={[CharacterVerify]}>
                  <Input placeholder={t("ISMS.InputFileName(Optional)")} maxLength={30} />
                </Form.Item>
                {/* 文件類型選擇 */}
                <Form.Item
                  label={t("ISMS.FileType")}
                  name={"type"}
                  rules={[{ required: true, message: "Please select a type" }]}
                >
                  <Select
                    placeholder={t("ISMS.Select")}
                    onChange={(value: any) => {
                      setUploadFileType(value);
                    }}
                  >
                    {/* 選擇文件類型: 用戶需要選擇文件的類型，可以選擇以下選項之一： 
                        "Config"（cfg）: 用於存儲配置文件。
                        "Firmware"（fw）: 用於存儲固件文件。這個選項可能會有一些特殊行為，特別是涉及到 MD5 哈希值的計算。
                        "YAML"（yml）: 用於存儲 YAML 文件。
                        "Others"（any）: 用於存儲其他類型的文件。*/}
                    <OptGroup label={t("ISMS.Restore")}>
                      <Option value="cfg" disabled={level === "set"}>
                        Config-(cfg)
                      </Option>
                    </OptGroup>
                    <OptGroup label={t("ISMS.Upgrade")}>
                      <Option value="fw" disabled={level === "set"}>
                        Firmware-(fw)
                      </Option>
                    </OptGroup>
                    <OptGroup label="YAML">
                      <Option value="yml">YAML-(yml)</Option>
                    </OptGroup>
                    <OptGroup label="Others">
                      <Option value="Others">Others-(any)</Option>
                    </OptGroup>
                  </Select>
                </Form.Item>

                <Form.Item style={uploadFileType !== "fw" ? { display: "none" } : {}} label={"MD5"} name={"md5"}>
                  <Input disabled={true} maxLength={30} />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </Modal>
    );
  }
);

export default UploadFileModal;
